import {Autocomplete, Paper, PaperProps, TextField} from '@mui/material'
import React, {FC, ReactNode, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {INPUT_FORM_FIELD_SX} from '../../consts'
import {useKpiNodeIds} from '../../hooks'
import {KpiOption} from '../../types'
import {areMainEquipmentsSame, getTranslationKey} from '../../utils'

const paper = (props: PaperProps) => <Paper elevation={8} style={{minWidth: '350px'}} {...props} />

function getDisplayPath(equipment: KpiOption) {
  return (
    equipment && (
      <span data-test-id={`stopped-main-equipment-search-suggestion-${equipment.id}`}>
        {equipment.label}
      </span>
    )
  )
}

type StoppedMainEquipmentProps = {
  plantId: string
  label?: ReactNode
  errorText?: ReactNode
  onChange: (equipment?: string) => void
  value?: string
  required?: boolean
  disabled?: boolean
  translationPrefix?: string
  availableDepartmentCodes?: string[]
}

export const StoppedMainEquipment: FC<StoppedMainEquipmentProps> = ({
  plantId,
  label,
  errorText,
  onChange,
  value,
  required,
  disabled,
  translationPrefix,
  availableDepartmentCodes
}) => {
  const {t} = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const departments = useKpiNodeIds(plantId, availableDepartmentCodes)
  const selectedDepartment = departments.find(({id}) => id === value)

  const valueLabel = selectedDepartment?.label
  const filteredDepartments = inputValue
    ? departments.filter((dept) => dept.label.toLowerCase().includes(inputValue.toLowerCase()))
    : departments

  useEffect(() => {
    setInputValue(valueLabel ?? '')
  }, [valueLabel, setInputValue])

  useEffect(() => {
    if (departments.length === 1) {
      onChange(departments[0].id)
    }
  }, [departments, onChange])

  return (
    <Autocomplete
      data-test-id="stopped-main-equipment-search"
      value={selectedDepartment ?? null}
      inputValue={inputValue}
      open={open && filteredDepartments.length > 0}
      onOpen={() => {
        setOpen(true)
      }}
      onChange={(event, next, reason) => {
        if (reason === 'clear') {
          setInputValue('')
          onChange(undefined)
          return
        }
        if (next && typeof next !== 'string') {
          onChange(next.id)
        }
      }}
      onClose={() => {
        setOpen(false)
      }}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input' && newValue !== inputValue) {
          setInputValue(newValue)

          if (!newValue && value) {
            onChange(undefined)
          }
        }
      }}
      options={filteredDepartments}
      getOptionLabel={({label}) => label}
      isOptionEqualToValue={areMainEquipmentsSame}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            error={!!errorText}
            data-test-id="stopped-main-equipment-search-input"
            helperText={errorText}
            label={label}
            required={required}
            placeholder={t(getTranslationKey('pleaseStartTyping', translationPrefix))}
            variant="filled"
            InputProps={{...params.InputProps, ...INPUT_FORM_FIELD_SX}}
          />
        )
      }}
      PaperComponent={paper}
      forcePopupIcon={false}
      renderOption={(props, option) => <li {...props}>{getDisplayPath(option)}</li>}
      // per default the auto-compleat component will use the getOptionLabel,
      // compare it with the given input text and will filter out all that do not match.
      // that is a problem when having sapNumbers were we remove the leading zeros
      // 00000008321003 will be rendered as 8321003 and therefor not match if the input is 0083
      // and will not be presented as an option
      // to avoid this behavior, we override the filterOptions function to force it to compare on object level
      filterOptions={(options) => options}
      disabled={disabled}
    />
  )
}
